import React, { useEffect, useState, Fragment } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import Layout from 'components/sales/layout/layout';
import localization from 'assets/localization/localization';
import { Button } from 'react-bootstrap';
import CardSection from 'components/sales/cardSection/cardSection';
import DivLayout from 'components/sales/divLayout/divLayout';
import ModalPop from "components/sales/modal/modal";
import FileUpload from "components/sales/fileUpload/fileUpload";
import { toast } from "react-toastify";
import ActivityTimeline from 'components/sales/activityTimeline/ActivityTimeline';
import AccordionItem from 'components/sales/accordion/AccordionItem';
import './submitJMS.css';

const SubmitJMS = ({
  getJmsDetails,
  submitJmsData,
  approveJmsData,
  documentUploadUrlPublic,
  documentUrlDataPublic
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const { site_id, jms_id } = useParams();
  const history = useHistory();

  const [apiData, setApiData] = useState(null);
  const [jmsLineItems, setJmsLineItems] = useState([]);
  const [globalBoqData, setGlobalBoqData] = useState([]);
  const [poDetailsList, setPoDetailsList] = useState([]);
  const [fullPoList, setFullPoList] = useState([]);
  const [historyData, setHistoryData] = useState([]);
  const [jmsDetailsInfo, setJmsDetailsInfo] = useState(null);
  
  const [showActionModal, setShowActionModal] = useState(false);
  const [uploadedDoc, setUploadedDoc] = useState("");
  const [activePoIndex, setActivePoIndex] = useState(null);

  const [showApproveModal, setShowApproveModal] = useState(false);
  const [approveAction, setApproveAction] = useState("");
  const [approvalRemarks, setApprovalRemarks] = useState("");
  const [approvalScope, setApprovalScope] = useState("");

  const roleId = window.localStorage.getItem("fusion-role");
  const isSales = Number(roleId) === 83;

  const [statusConfig, setStatusConfig] = useState({
    can_edit_boq: false,
    show_approve_button: false,
    show_reject_button: false,
    newly_available_po_ids: []
  });

  const [openAccordions, setOpenAccordions] = useState({
    history: false,
    po: {}
  });

  const togglePoAccordion = (poId) => {
    setOpenAccordions(prev => ({
      ...prev,
      po: {
        ...prev.po,
        [poId]: !prev.po[poId]
      }
    }));
  };

  useEffect(() => {
    fetchData();
  }, [site_id]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);

    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const res = await getJmsDetails({ site_id, ...(jms_id && { jms_id }) });
      if (res && res.data && res.data.data) { 
        setApiData(res.data.data); 
      } else {
        setApiData(null);
      }
    } catch {
      toast.error('Failed to load JMS details');
    } finally {
      setIsLoading(false);
    }
  };

  const calculateGst = (item, qty) => {
    const gstRate = item.gst !== undefined ? Number(item.gst) : (Number(item.gst_percentage) || Number(item.gst_percent) || 0);
    const unitRate = item.unit_rate !== undefined ? Number(item.unit_rate) : (Number(item.rate) || Number(item.rates) || 0);
    const amount = Number(unitRate) * Number(qty);
    
    // Percentages
    let igst_percent = Number(item.igst_percent) || 0;
    let cgst_percent = Number(item.cgst_percent) || 0;
    let sgst_percent = Number(item.sgst_percent) || 0;

    if (gstRate > 0 && igst_percent === 0 && cgst_percent === 0 && sgst_percent === 0) {
      igst_percent = gstRate;
    }

    // Amounts
    const igst_amount = (amount * igst_percent) / 100;
    const cgst_amount = (amount * cgst_percent) / 100;
    const sgst_amount = (amount * sgst_percent) / 100;
    
    // Total GST amount (sum of components or total rate)
    let gst_amount = (amount * gstRate) / 100;
    if (igst_amount > 0 || cgst_amount > 0 || sgst_amount > 0) {
      gst_amount = igst_amount + cgst_amount + sgst_amount;
    }

    const product_total = amount + gst_amount;

    return {
      gst_amount: Number(gst_amount.toFixed(2)),
      igst_percent: igst_percent,
      igst_rate: Number(igst_amount.toFixed(2)),
      cgst_percent: cgst_percent,
      cgst_rate: Number(cgst_amount.toFixed(2)),
      sgst_percent: sgst_percent,
      sgst_rate: Number(sgst_amount.toFixed(2)),
      product_subtotal: Number(amount.toFixed(2)),
      product_total: Number(product_total.toFixed(2))
    };
  };

  useEffect(() => {
    if (!apiData) return;

    // Use top-level keys if available (preferred) or fall back to po_list aggregation
    const jmsItems = apiData.jms_line_items || [];
    const boqData = apiData.boq_data || [];
    const poList = apiData.po_list || [];
    
    // Permissions
    setStatusConfig({
      can_edit_boq: apiData.can_edit_boq ?? poList[0]?.can_edit_boq ?? false,
      show_approve_button: apiData.show_approve_button ?? poList[0]?.show_approve_button ?? false,
      show_reject_button: apiData.show_reject_button ?? poList[0]?.show_reject_button ?? false,
      newly_available_po_ids: apiData.newly_available_po_ids || poList[0]?.newly_available_po_ids || []
    });

    // PO Details aggregation
    const allPoDetails = poList.map(group => group.po_details).filter(Boolean);
    setPoDetailsList(allPoDetails);

    // Global Data
    setGlobalBoqData(boqData);
    setFullPoList(poList);
    setJmsDetailsInfo(apiData.jms_details || poList[0]?.jms_details || null);
    setHistoryData(apiData.history_data || poList[0]?.history_data || []);

    // Line Items normalization
    let initialLineItems = [];
    if (jmsItems.length > 0) {
      initialLineItems = jmsItems.map(item => {
        const standardRate = item.unit_rate ?? item.rate ?? item.rates ?? 0;
        const q = item.quantity ?? item.qty ?? 0;
        const calculations = calculateGst({ ...item, unit_rate: standardRate }, q);
        return {
          ...item,
          unit_rate: standardRate,
          quantity: q,
          gst_percent: item.gst_percentage ?? item.gst ?? item.gst_percent ?? 0,
          ...calculations
        };
      });
    } else {
      // If no jms_line_items, fall back to material_service from all POs (new submission)
      poList.forEach(group => {
        const rawItems = group.material_service || [];
        rawItems.forEach(item => {
          const standardRate = item.unit_rate ?? item.rate ?? item.rates ?? 0;
          const q = item.quantity ?? item.qty ?? 0;
          const calculations = calculateGst({ ...item, unit_rate: standardRate }, q);
          initialLineItems.push({
            ...item,
            unit_rate: standardRate,
            quantity: q,
            gst_percent: item.gst_percentage ?? item.gst ?? item.gst_percent ?? 0,
            ...calculations
          });
        });
      });
    }
    setJmsLineItems(initialLineItems);

  }, [apiData]);

  const handleQtyChange = (itemIndex, value) => {
    const updatedItems = [...jmsLineItems];
    const item = updatedItems[itemIndex];
    if (item) {
      const qty = Number(value);
      item.quantity = qty;
      const calculations = calculateGst(item, qty);
      updatedItems[itemIndex] = {
        ...item,
        ...calculations
      };
      setJmsLineItems(updatedItems);
    }
  };

  const handleDelete = (itemIndex) => {
    const updatedItems = [...jmsLineItems];
    updatedItems.splice(itemIndex, 1);
    setJmsLineItems(updatedItems);
  };

  const handleAddBoqFromDropdown = (serviceId) => {
    if (!serviceId) return;

    const boqItem = globalBoqData?.find(s => s.id == serviceId);
    if (!boqItem) return;

    const existingIndex = jmsLineItems.findIndex(b => 
      (b.material_service_id == boqItem.service_id || b.id == boqItem.id) && 
      Number(b.unit_rate) === Number(boqItem.rates)
    );
    
    if (existingIndex !== -1) {
      const updatedItems = [...jmsLineItems];
      const newQty = (updatedItems[existingIndex].quantity || 0) + 1;
      const calculations = calculateGst(boqItem, newQty);
      
      updatedItems[existingIndex] = {
        ...updatedItems[existingIndex],
        quantity: newQty,
        ...calculations
      };
      setJmsLineItems(updatedItems);
      toast.info(`Increased quantity for ${boqItem.service_name || boqItem.name}`);
    } else {
      const calculations = calculateGst(boqItem, 1);
      const newItem = {
        po_id: boqItem.source_po_id || poDetailsList[0]?.id || "", 
        boq_id: boqItem.id,
        material_service_id: boqItem.service_id,
        name: boqItem.service_name || boqItem.name,
        description: boqItem.description,
        code: boqItem.service_code || boqItem.code,
        type: boqItem.type || 2,
        quantity: 1,
        rate: boqItem.rates || boqItem.unit_rate,
        gst_percent: boqItem.gst !== undefined ? boqItem.gst : (boqItem.gst_percentage || boqItem.gst_percent || 0),
        hsn: boqItem.hsn_code || boqItem.hsn,
        uom: boqItem.uom_name,
        unit_rate: Number(boqItem.rates || boqItem.unit_rate),
        ...calculations
      };

      setJmsLineItems(prev => [...prev, newItem]);
      toast.success(`Added ${boqItem.service_name || boqItem.name}`);
    }
  };

  const handleApproveAction = async () => {
    try {
      setIsLoading(true);
      if (!jmsDetailsInfo?.id) {
        toast.error("JMS record not found.");
        setIsLoading(false);
        return;
      }

      if (isSales && approveAction === "APPROVE" && !approvalScope) {
        toast.error("Please select a Scope.");
        setIsLoading(false);
        return;
      }

      const payload = {
        id: jmsDetailsInfo.id,
        action: approveAction,
        remarks: approvalRemarks,
        scope: isSales ? approvalScope : ""
      };

      const res = await approveJmsData(payload);
      if (res && (res.status === 0 || res.status === 200)) {
        toast.success(`JMS ${approveAction === "APPROVE" ? "approved" : "rejected"} successfully!`);
        setShowApproveModal(false);
        setApprovalRemarks("");
        setApprovalScope("");
        fetchData();
      } else {
        toast.error(res?.message || "Action failed");
      }
    } catch (err) {
      toast.error('Something went wrong');
    } finally {
      setIsLoading(false);
    }
  };

  const processFormulation = async () => {
    try {
      setIsLoading(true);
      
      if (jmsLineItems.length === 0) {
        toast.error("No items available to submit.");
        setIsLoading(false);
        return;
      }

      const hasInvalidQty = jmsLineItems.some(item => !item.quantity || item.quantity <= 0);
      if (hasInvalidQty) {
        toast.error("Quantity must be greater than 0 for all items.");
        setIsLoading(false);
        return;
      }

      let basePoIdStr = jmsDetailsInfo?.po_id || poDetailsList.map(po => po.id).filter(Boolean).join(',') || "";
      const lineItemPoIds = jmsLineItems.map(item => String(item.po_id || '')).filter(Boolean).flatMap(id => id.split(','));
      const allPoIds = [...new Set([...(basePoIdStr ? String(basePoIdStr).split(',') : []), ...lineItemPoIds])].filter(Boolean);
      const payload = {
        po_id: allPoIds.join(','),
        site_id: site_id,
        ...(jms_id && { jms_id }),
        document: uploadedDoc,
        items: jmsLineItems
      };

      const res = await submitJmsData(payload);
      if (res && (res.status === 0 || res.status === 200)) {
        toast.success("JMS Details submitted successfully!");
        setShowActionModal(false);
        setUploadedDoc("");
        if ([8, 9].includes(Number(roleId))) {
          setTimeout(() => {
            history.goBack();
          }, 2000);
        } else {
          fetchData();
        }
      } else {
        toast.error(res?.message || "Submit failed");
      }
    } catch (err) {
      toast.error('Something went wrong');
    } finally {
      setIsLoading(false);
    }
  };

  const headerBtns = () => {
    const showSubmitBtn = statusConfig.can_edit_boq;
    return (
      <Fragment>
        {showSubmitBtn && (
          <Button
            className="btn btn-primary mr-3"
            onClick={() => {
              setActivePoIndex("all");
              setShowActionModal(true);
            }}
          >
            Submit JMS
          </Button>
        )}
        
        {jmsDetailsInfo && jmsDetailsInfo.id && (
          <>
            {statusConfig.show_approve_button && (statusConfig.newly_available_po_ids || []).length === 0 && (
              <Button
                className="btn btn-success mr-3"
                onClick={() => {
                  setApproveAction("APPROVE");
                  setShowApproveModal(true);
                }}
              >
                Approve
              </Button>
            )}
            {statusConfig.show_reject_button && (
              <Button
                className="btn btn-danger mr-3"
                onClick={() => {
                  setApproveAction("REJECT");
                  setShowApproveModal(true);
                }}
              >
                Send Back For Modification
              </Button>
            )}
          </>
        )}
      </Fragment>
    );
  };

  return (
    <Layout
      title="Submit JMS"
      isLoading={isLoading}
      isGoback
      Buttons={headerBtns}
      linkUrl={``}
    >
    {(!apiData || !apiData.po_list || apiData.po_list.length === 0) && !isLoading && !jmsDetailsInfo && (
        <CardSection>
           <h5 className="text-center p-4">No Data Available to initialize JMS.</h5>
        </CardSection>
      )}

      {/* JMS Details Accordion (if editing) */}
      {jmsDetailsInfo && (
        <div className="mb-4">
          <AccordionItem
            title={`JMS Details (Status: ${jmsDetailsInfo.status})`}
            isOpen={!!openAccordions.jms}
            onToggle={() => setOpenAccordions(prev => ({ ...prev, jms: !prev.jms }))}
          >
            <div className="details-listing dl-view">
              <DivLayout heading="PO ID(s)" data={jmsDetailsInfo.po_id} mkClass="details-list col-md-4" />
              <DivLayout heading="Site No" data={jmsDetailsInfo.site_no} mkClass="details-list col-md-4" />
              <DivLayout heading="Billing Scope" data={jmsDetailsInfo.scope || 'N/A'} mkClass="details-list col-md-4" />
              <DivLayout heading="Created By" data={jmsDetailsInfo.created_by} mkClass="details-list col-md-4" />
              <DivLayout heading="Created At" data={formatDate(jmsDetailsInfo.created_at)} mkClass="details-list col-md-4" />
              {jmsDetailsInfo.document && (
                <DivLayout heading="Document" data={
                  <a href={jmsDetailsInfo.document} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-outline-primary">
                    <i className="fa fa-file-text-o mr-1"></i> View Doc
                  </a>
                } mkClass="details-list col-md-4" />
              )}
            </div>
          </AccordionItem>
        </div>
      )}

      {/* ASSOCIATED POs SECTION */}
      {poDetailsList.length > 0 && (
        <div className="mb-5">
           <div className="section-label-horizontal mb-3 ml-1">
            Associated Purchase Orders ({apiData?.site_name})
           </div>
           <div className="po-accordions-container">
             {poDetailsList.map((po, idx) => {
               const poId = po?.id || idx;
               const isPoOpen = !!openAccordions.po[poId];
               const fullPo = fullPoList.find(p => p.po_details?.id === poId);
               const poItems = fullPo?.material_service || [];

               return (
                 <AccordionItem
                   key={poId}
                   title={`PO: ${po?.po_no || 'NA'} ${po?.is_new_po ? '(New PO)' : ''}`}
                   isOpen={isPoOpen}
                   onToggle={() => togglePoAccordion(poId)}
                 >
                   <div className="details-listing dl-view mb-4">
                      <DivLayout heading="PR Number" data={po?.pr_no} mkClass="details-list col-md-3" />
                      <DivLayout heading="Site No" data={po?.site_no} mkClass="details-list col-md-3" />
                      <DivLayout heading="Sub Total" data={po?.sub_total} mkClass="details-list col-md-3" />
                      <DivLayout heading="Total Tax" data={po?.total_tax} mkClass="details-list col-md-3" />
                      <DivLayout heading="Total Amount" data={po?.total_amount} mkClass="details-list col-md-3" />
                      <DivLayout heading="Created By" data={po?.created_by} mkClass="details-list col-md-3" />
                      <DivLayout heading="Requested By" data={po?.requested_by} mkClass="details-list col-md-3" />
                      <DivLayout heading="LOB" data={po?.lob} mkClass="details-list col-md-3" />
                   </div>

                   {poItems.length > 0 && (
                     <div className="po-original-items mt-3">
                       <h6 className="font-weight-bold grey-header-text mb-3 px-2">PO Material / Services</h6>
                       <div className="table-responsive">
                         <table className="table table-sm table-bordered">
                           <thead className="bg-light">
                             <tr>
                               <th className="text-center">S.No</th>
                               <th>Name</th>
                               <th className="text-center">UOM</th>
                               <th className="text-right">Rate</th>
                               <th className="text-center">Qty</th>
                               <th className="text-center">GST %</th>
                               <th className="text-right">Total</th>
                             </tr>
                           </thead>
                           <tbody>
                             {poItems.map((pi, pidx) => (
                               <tr key={pidx}>
                                 <td className="text-center">{pidx + 1}</td>
                                 <td>{pi.name}</td>
                                 <td className="text-center">{pi.uom}</td>
                                 <td className="text-right">₹{Number(pi.unit_rate).toLocaleString('en-IN')}</td>
                                 <td className="text-center">{pi.quantity}</td>
                                 <td className="text-center">{pi.gst_percentage || pi.gst_percent || 0}%</td>
                                 <td className="text-right">₹{Number(pi.total_amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                               </tr>
                             ))}
                           </tbody>
                         </table>
                       </div>
                     </div>
                   )}
                 </AccordionItem>
               );
             })}
           </div>
        </div>
      )}

      {/* SINGLE CONSOLIDATED JMS LINE ITEMS CARD */}
      {jmsLineItems.length > 0 && (
        <div className="mb-5">
          <CardSection
            clsName="material-service-header"
            title={
              <div className="d-flex align-items-center w-100 justify-content-between">
                <span className="header-title-text"><i className="fa fa-list mr-2"></i>JMS Line Items</span>
                {statusConfig.can_edit_boq && (
                  <div className="header-controls-container d-flex align-items-center" style={{ gap: '15px' }}>
                    <select
                      className="form-control premium-dropdown"
                      onChange={(e) => handleAddBoqFromDropdown(e.target.value)}
                      value=""
                      style={{ width: '320px' }}
                    >
                      <option value="" disabled>+ Add Material / Service from BOQ</option>
                      {globalBoqData?.map((item, idx) => (
                        <option key={idx} value={item.id}>
                          {item.service_name} (Rate: {item.rates})
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            }
          >
            <div className="table-responsive pr-table-container custom-scrollbar">
              <table className="table table-bordered pr-table mb-0">
                <thead>
                  <tr>
                    <th>S.No</th> 
                    <th>Name</th> 
                    <th>UOM</th> 
                    <th>Rate</th> 
                    <th>PO Qty</th> 
                    <th>Qty</th> 
                    <th>CGST%</th>
                    <th>CGST Amt</th>
                    <th>SGST%</th>
                    <th>SGST Amt</th>
                    <th>IGST%</th>
                    <th>IGST Amt</th>
                    <th>Sub Total</th>
                    <th>Total Amnt</th>
                    {statusConfig.can_edit_boq && <th>Action</th>}
                  </tr>
                </thead>
                <tbody>
                  {jmsLineItems.map((item, index) => (
                    <tr key={index}>
                      <td className="text-center">{index + 1}</td>
                      <td>
                        <div className="d-flex flex-column">
                          <span className="font-weight-bold">{item.name}</span>
                          <span className="text-muted small">Code: {item.material_service_id || item.code}</span>
                        </div>
                      </td>
                      <td className="text-center">{item.uom_name || item.uom}</td>
                      <td className="text-right">₹{Number(item.unit_rate).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                      <td className="text-center">{item.po_quantity || 0}</td>
                      <td style={{ width: "100px" }}>
                        {statusConfig.can_edit_boq ? (
                          <input
                            type="text"
                            className="qty-input"
                            value={item.quantity || ""}
                            onChange={(e) => {
                              const val = e.target.value.replace(/[^0-9]/g, '');
                              handleQtyChange(index, parseInt(val, 10) || 0)
                            }}
                          />
                        ) : (
                          <div className="text-center font-weight-bold" style={{ color: '#000' }}>{item.quantity}</div>
                        )}
                      </td>
                      <td className="text-center">{item.cgst_percent || 0}%</td>
                      <td className="text-right">₹{Number(item.cgst_rate || 0).toFixed(2)}</td>
                      <td className="text-center">{item.sgst_percent || 0}%</td>
                      <td className="text-right">₹{Number(item.sgst_rate || 0).toFixed(2)}</td>
                      <td className="text-center">{item.igst_percent || 0}%</td>
                      <td className="text-right">₹{Number(item.igst_rate || 0).toFixed(2)}</td>
                      <td className="text-right font-weight-bold">₹{Number(item.product_subtotal || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                      <td className="text-right font-weight-bold text-primary">₹{Number(item.product_total || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                      {statusConfig.can_edit_boq && (
                        <td className="text-center">
                          <Button 
                            variant="danger" 
                            size="sm"
                            className="btn-sm"
                            style={{ fontSize: '11px', padding: '2px 8px' }}
                            onClick={() => handleDelete(index)}
                            title="Delete Item"
                          >
                            Delete
                          </Button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardSection>
        </div>
      )}

      {/* CONSOLIDATED SUMMARY SECTION */}
      {jmsLineItems.length > 0 && (
        <div className="mb-5 mt-4">
          <CardSection title="Consolidated JMS Summary" clsName="grand-summary-header" panelN="grand-summary-panel">
            <div className="row m-0 justify-content-between p-3">
              <div className="col-md-3 p-2">
                <div className="summary-item-card">
                  <div className="summary-label">Items Count</div>
                  <div className="summary-value h4 font-weight-bold mb-0">{jmsLineItems.length}</div>
                </div>
              </div>
              
              <div className="col-md-3 p-2 border-left">
                <div className="summary-item-card">
                  <div className="summary-label">Total Sub-Total</div>
                  <div className="summary-value h4 font-weight-bold mb-0">₹{jmsLineItems.reduce((acc, item) => 
                    acc + (Number(item.product_subtotal) || 0)
                  , 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
                </div>
              </div>

              <div className="col-md-3 p-2 border-left">
                <div className="summary-item-card">
                  <div className="summary-label">Total Tax Amount</div>
                  <div className="summary-value h4 font-weight-bold mb-0">₹{jmsLineItems.reduce((acc, item) => 
                    acc + (Number(item.gst_amount) || 0)
                  , 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
                </div>
              </div>

              <div className="col-md-3 p-2 border-left text-primary">
                <div className="summary-item-card">
                  <div className="summary-label">Final Grand Total</div>
                  <div className="summary-value h4 font-weight-bold mb-0">₹{jmsLineItems.reduce((acc, item) => 
                    acc + (Number(item.product_total) || 0)
                  , 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
                </div>
              </div>
            </div>
          </CardSection>
        </div>
      )}

      {/* HISTORY ACCORDION */}
      {historyData && historyData.length > 0 && (
        <div className="mb-5">
          <AccordionItem
            title={`Activity History Timeline`}
            isOpen={!!openAccordions.history}
            onToggle={() => setOpenAccordions(prev => ({ ...prev, history: !prev.history }))}
          >
            <ActivityTimeline data={historyData} />
          </AccordionItem>
        </div>
      )}

      <ModalPop
        title="Submit JMS Confirmation"
        show={showActionModal}
        handleModalHide={() => {
          setShowActionModal(false);
          setUploadedDoc("");
          setActivePoIndex(null);
        }}
      >
        <div className="text-center">
          <h4>Are you sure you want to submit JMS {activePoIndex === "all" ? "for all POs" : "for this PO"}?</h4>

          <div className="row justify-content-center mt-4">
            <div className="col-md-8 mx-auto">
              <FileUpload
                id="jmsDocument"
                accept=".pdf,.png,.jpg,.jpeg,.doc"
                documentUploadUrl={documentUploadUrlPublic} 
                documentUrlData={documentUrlDataPublic}
                onChange={(val) => setUploadedDoc(val)}
                width="100%"
              >
                <span className="btn btn-transparent border w-100 mt-4 d-flex align-items-center justify-content-center">
                  <i className="fa fa-upload mr-2"></i> Select Document
                </span>
              </FileUpload>
              {uploadedDoc && <div className="mt-2 text-success">Document securely uploaded!</div>}
            </div>
          </div>

          <div className="d-flex justify-content-center mt-5">
            <Button
              className="btn btn-danger mr-3"
              onClick={() => {
                setShowActionModal(false);
                setUploadedDoc("");
                setActivePoIndex(null);
              }}
            >
              Cancel
            </Button>

            <Button
              className="btn btn-success ml-3"
              onClick={() => processFormulation(activePoIndex)}
            >
              Submit
            </Button>
          </div>
        </div>
      </ModalPop>

      <ModalPop
        title={`Confirm ${approveAction === "APPROVE" ? "Approval" : "Rejection"}`}
        show={showApproveModal}
        handleModalHide={() => {
          setShowApproveModal(false);
          setApprovalRemarks("");
          setApprovalScope("");
        }}
      >
        <div className="text-center">
          <h4>
            Are you sure you want to {approveAction === "APPROVE" ? "approve" : "send back"} this JMS?
          </h4>

          {isSales && approveAction === "APPROVE" && (
            <div className="row justify-content-center mt-4">
              <div className="col-md-10 mx-auto text-left">
                <label className="font-weight-bold">Scope <span className="text-danger">*</span></label>
                <select 
                  className="form-control"
                  value={approvalScope}
                  onChange={(e) => setApprovalScope(e.target.value)}
                >
                  <option value="">Select Scope</option>
                  <option value="Client">Yes (Client will pay)</option>
                  <option value="Park+">No (to be borne by Park+)</option>
                </select>
              </div>
            </div>
          )}

          <div className="row justify-content-center mt-4">
            <div className="col-md-10 mx-auto text-left">
              <label className="font-weight-bold">Remarks (Optional)</label>
              <textarea
                className="form-control"
                rows="3"
                placeholder="Enter remarks..."
                value={approvalRemarks}
                onChange={(e) => setApprovalRemarks(e.target.value)}
              />
            </div>
          </div>

          <div className="d-flex justify-content-center mt-5">
            <Button
              className="btn btn-secondary mr-3"
              onClick={() => {
                setShowApproveModal(false);
                setApprovalRemarks("");
                setApprovalScope("");
              }}
            >
              Cancel
            </Button>

            <Button
              className={`btn ${approveAction === "APPROVE" ? "btn-success" : "btn-danger"} ml-3`}
              onClick={handleApproveAction}
            >
              Confirm
            </Button>
          </div>
        </div>
      </ModalPop>
    </Layout>
  );
};

export default SubmitJMS;
